import { useCallback, useRef } from 'react';

/**
 * Returns a function whose identity never changes but which always invokes the
 * latest version of `callback`. This is the "useEvent" pattern.
 *
 * Use it to pass handlers to `React.memo`'d children without forcing them to
 * re-render on every parent render (as a fresh closure would) and without the
 * stale-closure risk of `useCallback([])`. The wrapper always calls the most
 * recent closure, so it reads up-to-date props/state at call time.
 *
 * Note: like `useEvent`, the stable function must not be called during render —
 * only in effects, event handlers, or other post-render callbacks.
 */
export function useStableCallback<TArgs extends unknown[], TReturn>(
  callback: (...args: TArgs) => TReturn,
): (...args: TArgs) => TReturn {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  return useCallback((...args: TArgs) => callbackRef.current(...args), []);
}
